<script setup lang="ts">
import type { PolicyDocumentAction } from '#layers/access';
import type { ButtonProps } from '#layers/ui';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';

type RuleInformationType = 'none' | 'all' | 'custom';

export interface PolicyActionCellRuleInformation {
	type: RuleInformationType;
	hiddenFieldCount?: number;
	itemFilterConditionCount?: number;
	fieldFilterCount?: number;
}

interface PolicyActionCellProps {
	action: PolicyDocumentAction;
	collectionName: string;
	ruleInformation: PolicyActionCellRuleInformation;
}

const props = defineProps<PolicyActionCellProps>();
const emit = defineEmits<{
	noAccess: [];
	allAccess: [];
	customAccess: [];
}>();

const { t } = useI18n();
const appConfig = useAppConfig();

const ruleType = computed<PolicyActionCellRuleInformation['type']>(() => props.ruleInformation.type);

const filterCount = computed(() => {
	return (props.ruleInformation?.itemFilterConditionCount ?? 0)
		+ (props.ruleInformation?.fieldFilterCount ?? 0);
});

const hiddenFieldCount = computed(() => {
	return props.ruleInformation?.hiddenFieldCount ?? 0;
});

function getVariant(type: PolicyActionCellRuleInformation['type'], activeType: PolicyActionCellRuleInformation['type']) {
	if (type === activeType) return 'subtle';
	else return 'outline';
}

function getColor(type: PolicyActionCellRuleInformation['type'], activeType: PolicyActionCellRuleInformation['type'], activeColor: ButtonProps['color']) {
	if (type === activeType) return activeColor;
	else return 'neutral';
}

function getBindings(type: PolicyActionCellRuleInformation['type'], activeType: PolicyActionCellRuleInformation['type'], activeColor: ButtonProps['color']): any {
	const iconMap = { all: appConfig.ui.icons.check, none: 'lucide:ban', custom: 'lucide:settings-2' };
	return {
		color: getColor(type, activeType, activeColor),
		variant: getVariant(type, activeType),
		icon: iconMap[type],
	};
}

function getTooltipText(type: RuleInformationType, action: PolicyDocumentAction, collectionName: string) {
	if (type === 'all') {
		return t('policy.policy_rules.full_action_access_tooltip', { action: t(`policy.action.${action}`), collectionName });
	}

	if (type === 'none') {
		return t('policy.policy_rules.none_action_access_tooltip', { action: t(`policy.action.${action}`), collectionName });
	}

	// Custom type tooltip text
	if ((props.ruleInformation?.hiddenFieldCount ?? 0) > 0 && props.ruleInformation.fieldFilterCount === 0) {
		return t('policy.policy_rules.custom_action_access_tooltip_hidden_fields', { n: props.ruleInformation.hiddenFieldCount });
	}
	else if ((props.ruleInformation?.hiddenFieldCount ?? 0) === 0 && (props.ruleInformation?.fieldFilterCount ?? 0) > 0) {
		return t('policy.policy_rules.custom_action_access_tooltip_filters', { n: props.ruleInformation.fieldFilterCount });
	}
	else if ((props.ruleInformation?.hiddenFieldCount ?? 0) > 0 && (props.ruleInformation?.fieldFilterCount ?? 0) > 0) {
		return `${t('policy.policy_rules.custom_action_access_tooltip_hidden_fields', { n: props.ruleInformation.hiddenFieldCount })}, ${t('policy.policy_rules.custom_action_access_tooltip_filters', { n: props.ruleInformation.fieldFilterCount })}`;
	}
	else {
		return t('policy.policy_rules.custom_action_access_tooltip', { action: t(`policy.action.${action}`), collectionName });
	}
}
</script>

<template>
	<div class="relative flex flex-1 justify-center">
		<div class="group flex flex-1 items-center justify-center">
			<!-- Default state (not hovering) -->
			<div class="flex items-center group-hover:opacity-0">
				<DButton
					v-if="ruleInformation.type === 'all'"
					:icon="appConfig.ui.icons.check"
					size="sm"
					variant="link"
					color="success"
				/>
				<DButton
					v-else-if="ruleInformation.type === 'none'"
					icon="lucide:ban"
					size="sm"
					variant="link"
					class="text-dimmed"
				/>
				<DButton
					v-if="ruleInformation.type === 'custom' && filterCount > 0"
					:icon="appConfig.ui.icons.filter"
					size="sm"
					variant="link"
					color="warning"
				/>
				<DButton
					v-if="ruleInformation.type === 'custom' && hiddenFieldCount > 0"
					icon="lucide:lock-keyhole"
					size="sm"
					variant="link"
					color="warning"
				/>
			</div>

			<!-- Hover state (action buttons) -->
			<DFieldGroup class="absolute opacity-0 group-hover:opacity-100">
				<DTooltip :text="getTooltipText('all', action, collectionName)" :delay-duration="500">
					<DButton
						v-bind="getBindings(ruleType, 'all', 'success')"
						:icon="appConfig.ui.icons.check"
						size="sm"
						@click="emit('allAccess')"
					/>
				</DTooltip>
				<DTooltip :text="getTooltipText('none', action, collectionName)" :delay-duration="500">
					<DButton
						v-bind="getBindings(ruleType, 'none', 'neutral')"
						icon="lucide:ban"
						size="sm"
						@click="emit('noAccess')"
					/>
				</DTooltip>

				<DTooltip :text="getTooltipText('custom', action, collectionName)" :delay-duration="500">
					<DButton
						v-bind="getBindings(ruleType, 'custom', 'warning')"
						icon="lucide:settings-2"
						size="sm"
						@click="emit('customAccess')"
					/>
				</DTooltip>
			</DFieldGroup>
		</div>
	</div>
</template>
